import React from 'react';
import { CalendarRange } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  applyPresetWithComparison,
  detectActivePreset,
  formatPeriodLabel,
  type DiretoriaPeriodPreset,
} from '@/lib/diretoriaPeriod';

export interface DiretoriaPeriodBarProps {
  dataFrom: string;
  dataTo: string;
  compareFrom: string;
  compareTo: string;
  onChange: (primaryFrom: string, primaryTo: string, compareFrom: string, compareTo: string) => void;
  className?: string;
  /** Esconde o resumo textual (quando a página já exibe outro). */
  hideSummary?: boolean;
}

const dateInputClass = cn(
  'h-9 w-full min-w-0 rounded-md border border-input bg-background px-2 text-sm text-foreground',
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
);

function DateRangeGroup({
  title,
  from,
  to,
  onFromChange,
  onToChange,
}: {
  title: string;
  from: string;
  to: string;
  onFromChange: (value: string) => void;
  onToChange: (value: string) => void;
}) {
  return (
    <div className="flex min-w-0 flex-col gap-1.5 rounded-lg border border-border/80 bg-muted/20 px-3 py-2.5">
      <span className="text-[10px] font-semibold uppercase leading-none tracking-wide text-muted-foreground">
        {title}
      </span>
      <div className="grid grid-cols-1 gap-2 min-[380px]:grid-cols-2">
        <label className="flex min-w-0 flex-col gap-1">
          <span className="text-[11px] text-muted-foreground">De</span>
          <input
            type="date"
            value={from}
            max={to}
            onChange={(e) => onFromChange(e.target.value)}
            className={dateInputClass}
            aria-label={`${title} — data inicial`}
          />
        </label>
        <label className="flex min-w-0 flex-col gap-1">
          <span className="text-[11px] text-muted-foreground">Até</span>
          <input
            type="date"
            value={to}
            min={from}
            onChange={(e) => onToChange(e.target.value)}
            className={dateInputClass}
            aria-label={`${title} — data final`}
          />
        </label>
      </div>
    </div>
  );
}

export const DiretoriaPeriodBar: React.FC<DiretoriaPeriodBarProps> = ({
  dataFrom,
  dataTo,
  compareFrom,
  compareTo,
  onChange,
  className,
  hideSummary = false,
}) => {
  const activePreset = detectActivePreset(dataFrom, dataTo);

  const applyPreset = (preset: DiretoriaPeriodPreset) => {
    const { primary, comparison } = applyPresetWithComparison(preset);
    onChange(primary.dataFrom, primary.dataTo, comparison.dataFrom, comparison.dataTo);
  };

  return (
    <div className={cn('flex min-w-0 flex-col gap-2.5', className)}>
      <div
        className="grid grid-cols-3 gap-0.5 rounded-lg border border-border p-0.5"
        role="group"
        aria-label="Atalhos de período"
      >
        {(
          [
            ['hoje', 'Hoje'],
            ['semana', 'Semana'],
            ['mes', 'Mês'],
          ] as const
        ).map(([key, label]) => (
          <button
            key={key}
            type="button"
            onClick={() => applyPreset(key)}
            className={cn(
              'h-9 rounded-md px-1 text-xs font-medium transition-colors sm:px-3',
              activePreset === key
                ? 'bg-primary text-primary-foreground'
                : 'text-muted-foreground hover:bg-muted'
            )}
          >
            {label}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
        <DateRangeGroup
          title="Período"
          from={dataFrom}
          to={dataTo}
          onFromChange={(from) => onChange(from, dataTo, compareFrom, compareTo)}
          onToChange={(to) => onChange(dataFrom, to, compareFrom, compareTo)}
        />

        <DateRangeGroup
          title="Comparar com"
          from={compareFrom}
          to={compareTo}
          onFromChange={(from) => onChange(dataFrom, dataTo, from, compareTo)}
          onToChange={(to) => onChange(dataFrom, dataTo, compareFrom, to)}
        />
      </div>

      {!hideSummary ? (
        <p className="flex items-start gap-1.5 text-[11px] leading-snug text-muted-foreground">
          <CalendarRange className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
          <span className="min-w-0 break-words">
            Período: <span className="font-medium text-foreground">{formatPeriodLabel(dataFrom, dataTo)}</span>
            {' · '}
            Comparando com{' '}
            <span className="font-medium text-foreground">
              {formatPeriodLabel(compareFrom, compareTo)}
            </span>
          </span>
        </p>
      ) : null}
    </div>
  );
};
